import React from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import InvoiceSignatureButton from './InvoiceSignatureButton';
import ProcessEInvoicingQueueButton from './ProcessEInvoicingQueueButton';
import DownloadAgentButton from './DownloadAgentButton';
import invoicesAPI from '../../services/invoicesAPI';
import { Badge } from '../ui';
import {
  IconShieldCheck, IconSignature, IconEye, IconEdit, IconFileCode,
  IconDeviceMobile, IconFileCheck, IconSend, IconFileText,
  IconCircleCheck, IconTrash,
} from '@tabler/icons-react';
import { showConfirm } from '../../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../../utils/toasts';

const InvoicesTable = ({
  facturesFiltrees, clients, filtres, editerFacture, supprimerFacture,
  onDownloadInvoicePDF, onDownloadXML, onViewInvoice, onUpdateFacture,
}) => {
  const { currentUser, eSignatureEnabled: tenantEsig, isCabinet, tenantType } = useAuth();
  const isMedical = tenantType === 'cabinet_medical';
  const { workspaceESignatureEnabled } = useWorkspace();
  const role = currentUser?.role;
  const isAdmin = role === 'admin' || role === 'client';

  // Pour les cabinets, la signature électronique vient du workspace ; pour les companies, du tenant
  const eSignatureEnabled = isCabinet ? workspaceESignatureEnabled : tenantEsig;

  const handlePreviewPDFPro = async (facture) => {
    try {
      const invoiceId = facture.id || facture._id;
      const token = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://app.accountintime.tn';
      document.body.style.cursor = 'wait';
      const headers = { 'Authorization': `Bearer ${token}` };
      if (workspaceId) headers['x-workspace-id'] = workspaceId;
      const response = await fetch(`${apiUrl}/api/invoices/${invoiceId}/pdf`, { method: 'GET', headers });
      if (!response.ok) throw new Error("Erreur lors de la generation du PDF.");
      const blob = await response.blob();
      window.open(window.URL.createObjectURL(blob), '_blank');
    } catch (error) { toastError(`Erreur : ${error.message}`); }
    finally { document.body.style.cursor = 'default'; }
  };

  const handleDownloadLegalXml = async (facture) => {
    try {
      const invoiceId = facture.id || facture._id;
      const token = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://app.accountintime.tn';
      const headers = { 'Authorization': `Bearer ${token}` };
      if (workspaceId) headers['x-workspace-id'] = workspaceId;
      const response = await fetch(`${apiUrl}/api/invoices/${invoiceId}/download-legal-xml`, { method: 'GET', headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Fichier introuvable ou non encore scelle.");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `LEGAL_${facture.number}.xml`;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (error) { toastError(`Info : ${error.message}`); }
  };

  const handleDownloadSignedXmlDirect = async (facture) => {
    try {
      const token = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id');
      const invoiceId = facture.id || facture._id;
      const apiUrl = process.env.REACT_APP_API_URL || 'https://app.accountintime.tn';
      const headers = { 'Authorization': `Bearer ${token}` };
      if (workspaceId) headers['x-workspace-id'] = workspaceId;
      const response = await fetch(`${apiUrl}/api/invoices/${invoiceId}/download-signed`, { method: 'GET', headers });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `SIGNED_${facture.number}.xml`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (error) { toastError("Erreur."); }
  };

  const renderEInvoicingStatus = (facture) => {
    const status = facture.eInvoicingStatus || facture.eInvoicing?.status;
    if (!status || status === 'NOT_SENT') return <Badge variant="gray">Non signee</Badge>;
    if (status === 'READY_TO_SIGN') return <Badge variant="warning">Prete a signer</Badge>;
    if (status === 'SIGNED') return <Badge variant="info">Signee (Locale)</Badge>;
    if (status === 'SENT_TTN') return <Badge variant="info">Envoyee TTN</Badge>;
    if (status === 'ACCEPTED') return <Badge variant="success">Acceptee TTN</Badge>;
    if (status === 'REJECTED') return <Badge variant="danger">Rejetee</Badge>;
    return <Badge variant="gray">{status}</Badge>;
  };

  const facturesTriees = [...facturesFiltrees].sort((a, b) =>
    (b.number || '').localeCompare(a.number || '', undefined, { numeric: true, sensitivity: 'base' })
  );

  const ActionBtn = ({ onClick, title, className, children }) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded-lg transition-colors border ${className}`}>
      {children}
    </button>
  );

  return (
    <>
      {isAdmin && eSignatureEnabled && (
        <div className="flex justify-end mb-3 gap-2 flex-wrap">
          <a href="/validation" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 text-sm font-medium no-underline">
            <IconShieldCheck size={15} strokeWidth={1.6} />
            Verifier Conformite
          </a>
          <button
            onClick={async () => { if (await showConfirm("Signer tout ?")) { await invoicesAPI.signBatchWithSeal(); window.location.reload(); } }}
            className="inline-flex items-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 text-sm font-medium">
            <IconSignature size={15} strokeWidth={1.6} />
            Signer tout (Seal)
          </button>
          <DownloadAgentButton />
          <ProcessEInvoicingQueueButton />
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Numero', 'Date', 'Client', 'Total', 'Statut', 'E-facture', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {facturesTriees.map((facture) => {
              const invoiceId = facture.id || facture._id;
              const eStatus = facture.eInvoicingStatus || facture.eInvoicing?.status;
              const isSigned = ['SIGNED', 'SENT_TTN', 'ACCEPTED'].includes(eStatus);
              const isSent   = ['SENT_TTN', 'ACCEPTED'].includes(eStatus);

              let dateFacture = '-';
              if (facture.dateEmise) {
                dateFacture = new Date(facture.dateEmise).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
              } else if (facture.date) {
                dateFacture = facture.date.includes('T')
                  ? new Date(facture.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                  : facture.date;
              }

              return (
                <tr key={invoiceId} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-sm font-semibold text-gray-800">{facture.number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{dateFacture}</td>
                  <td className="px-4 py-3 text-sm text-gray-800">{facture.clientId?.companyName || 'Client'}</td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900">{(facture.totalTTC || 0).toFixed(3)} DT</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium badge-${facture.status}`}>
                      {facture.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{renderEInvoicingStatus(facture)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 items-center flex-wrap">
                      <ActionBtn onClick={() => handlePreviewPDFPro(facture)} title="Voir PDF" className="text-blue-600 hover:bg-blue-50 border-blue-200">
                        <IconEye size={14} strokeWidth={1.6} />
                      </ActionBtn>

                      {isAdmin && (
                        <ActionBtn onClick={() => editerFacture(facture)} title="Modifier" className="text-amber-600 hover:bg-amber-50 border-amber-200">
                          <IconEdit size={14} strokeWidth={1.6} />
                        </ActionBtn>
                      )}

                      {facture.status === 'draft' && (
                        <ActionBtn
                          onClick={async () => {
                            if (await showConfirm("Marquer cette facture comme envoyée ?")) {
                              await invoicesAPI.markAsSent(invoiceId);
                              window.location.reload();
                            }
                          }}
                          title="Marquer envoyée"
                          className="text-green-600 hover:bg-green-50 border-green-200"
                        >
                          <IconSend size={14} strokeWidth={1.6} />
                        </ActionBtn>
                      )}

                      <ActionBtn onClick={() => onDownloadXML?.(facture)} title="XML TEIF" className="text-orange-600 hover:bg-orange-50 border-orange-200">
                        <IconFileCode size={14} strokeWidth={1.6} />
                      </ActionBtn>

                      {!isSigned && eSignatureEnabled && (
                        <>
                          <ActionBtn onClick={() => {}} title="DigiGo" className="text-purple-600 hover:bg-purple-50 border-purple-200">
                            <IconDeviceMobile size={14} strokeWidth={1.6} />
                          </ActionBtn>
                          <InvoiceSignatureButton facture={facture} onUpdateFacture={onUpdateFacture} />
                        </>
                      )}

                      {isSigned && (
                        <ActionBtn onClick={() => handleDownloadSignedXmlDirect(facture)} title="XML Signe" className="text-teal-600 hover:bg-teal-50 border-teal-200">
                          <IconFileCheck size={14} strokeWidth={1.6} />
                        </ActionBtn>
                      )}

                      {isSigned && !isSent && (
                        <ActionBtn onClick={async () => { if (await showConfirm("Envoyer a TTN ?")) { await invoicesAPI.sendToTTN(invoiceId); window.location.reload(); } }} title="Envoyer TTN" className="text-indigo-600 hover:bg-indigo-50 border-indigo-200">
                          <IconSend size={14} strokeWidth={1.6} />
                        </ActionBtn>
                      )}

                      {isSent && (
                        <>
                          <ActionBtn onClick={() => handleDownloadLegalXml(facture)} title="XML Legal" className="text-slate-600 hover:bg-slate-50 border-slate-200">
                            <IconFileText size={14} strokeWidth={1.6} />
                          </ActionBtn>
                          <IconCircleCheck size={18} strokeWidth={1.6} className="text-green-500" title="Transmis a la TTN" />
                        </>
                      )}

                      {!isSent && isAdmin && (
                        <ActionBtn
                          onClick={async () => { if (await showConfirm("Etes-vous sur de vouloir supprimer cette facture definitiquement ?")) supprimerFacture(invoiceId); }}
                          title="Supprimer"
                          className="text-red-600 hover:bg-red-50 border-red-200">
                          <IconTrash size={14} strokeWidth={1.6} />
                        </ActionBtn>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

InvoicesTable.propTypes = {
  facturesFiltrees: PropTypes.array.isRequired,
  clients: PropTypes.array,
  filtres: PropTypes.object,
  editerFacture: PropTypes.func.isRequired,
  supprimerFacture: PropTypes.func.isRequired,
  onDownloadInvoicePDF: PropTypes.func,
  onDownloadXML: PropTypes.func,
  onViewInvoice: PropTypes.func,
  onUpdateFacture: PropTypes.func,
};

export default InvoicesTable;
